import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { ProxyGraphClients, abgGraphqlClient } from '@atlassian/temp-client-utils';
import { ApprovalType, FetchTaskListQuery, TaskStatus, TaskType } from '@css-xp/apd-common';
import { useApplicationContext } from '@css-xp/base/common-context';
import { fetchTaskList } from '../queries';

interface PageProps {
  businessKey?: string | undefined | null;
  taskType?: string;
  taskStatus?: string;
  skip?: boolean;
  approvalType?: ApprovalType;
}

export const useTaskList = ({
  businessKey,
  taskType = TaskType.AllTasks,
  taskStatus = TaskStatus.Open,
  skip = false,
  approvalType,
}: PageProps) => {
  const [data, setData] = useState<FetchTaskListQuery | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [isProcessed, setProcessed] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [refetch, setRefetch] = useState<number>(0);
  const user = useApplicationContext();
  const client = React.useMemo(() => abgGraphqlClient(ProxyGraphClients.APD), []);

  const _refetch = useCallback(() => {
    setRefetch((r) => r + 1);
    setProcessed(false);
  }, []);

  useEffect(() => {
    if (!user || skip) return;
    (async () => {
      try {
        setLoading(true);
        const queryVariables = {
          staffId: user?.userId?.split('@')?.[0],
          taskType,
          taskStatus,
        } as any;
        if (businessKey) {
          queryVariables.businessKey = businessKey;
        }
        if (approvalType) {
          queryVariables.approvalType = approvalType;
        }
        const { data, error } = await client.query<FetchTaskListQuery>({
          query: fetchTaskList,
          variables: queryVariables,
          fetchPolicy: 'no-cache',
        });
        if (error) throw error;
        setData(data);
        setError(null);
      } catch (err) {
        setData(null);
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    })();
  }, [businessKey, user, client, taskType, taskStatus, skip, approvalType, refetch]);

  useEffect(() => {
    if (data || error) {
      setProcessed(true);
    }
  }, [data, error]);

  const hasOpenApprovalRequest = useMemo(() => {
    return data?.taskList?.tasks?.length ? true : false;
  }, [loading, data, error]);

  const approvalStatus = useMemo(() => {
    if (hasOpenApprovalRequest) {
      return data?.taskList?.tasks?.[0].approvalStatus;
    }
  }, [hasOpenApprovalRequest]);

  return {
    data,
    loading,
    error,
    hasOpenApprovalRequest,
    approvalStatus,
    isProcessed,
    refetch: _refetch,
  };
};
